import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) return { user: null, profile: null, company: null, role: null, isLoading: true }
  return ctx
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [company, setCompany] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
    if (error) return null
    return data
  }, [])

  const fetchCompany = useCallback(async (companyId) => {
    if (!companyId) return null
    const { data } = await supabase
      .from('companies')
      .select('*')
      .eq('id', companyId)
      .maybeSingle()
    return data
  }, [])

  const refreshProfile = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setUser(null); setProfile(null); setCompany(null)
      return
    }
    setUser(user)
    const p = await fetchProfile(user.id)
    setProfile(p)
    if (p?.company_id) {
      const c = await fetchCompany(p.company_id)
      setCompany(c)
    }
  }, [fetchProfile, fetchCompany])

  useEffect(() => {
    let mounted = true

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!mounted) return
      if (session?.user) {
        setUser(session.user)
        const p = await fetchProfile(session.user.id)
        if (!mounted) return
        setProfile(p)
        if (p?.company_id) {
          const c = await fetchCompany(p.company_id)
          if (!mounted) return
          setCompany(c)
        }
      }
      setIsLoading(false)
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (event === 'SIGNED_OUT' || !session?.user) {
          setUser(null); setProfile(null); setCompany(null)
          return
        }
        setUser(session.user)
        const p = await fetchProfile(session.user.id)
        setProfile(p)
        if (p?.company_id) {
          const c = await fetchCompany(p.company_id)
          setCompany(c)
        }
      })()
    })

    return () => { mounted = false; subscription?.unsubscribe() }
  }, [fetchProfile, fetchCompany])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null); setProfile(null); setCompany(null)
  }, [])

  const value = {
    user,
    profile,
    company,
    role: profile?.role || null,
    isLoading,
    signOut,
    refreshProfile,
    onboardingComplete: profile?.onboarding_complete || false,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
